import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, Send, Sparkles } from 'lucide-react';
import { formatIstDateTime } from '@gbv/utils';
import { cn, useToast } from '@gbv/ui';
import { api, ApiRequestError } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { OPENERS, type Answer } from './knowledge';
import { byId, search } from './match';
import { parseCommand, type Draft } from './commands';

/**
 * The in-app assistant.
 *
 * It searches the product and explains its rules. There is no model behind it
 * and no request leaves the browser — every answer here was written into the
 * app, and the matching is a scoring function over keywords.
 *
 * That shapes the whole design. It must never appear to be thinking, because
 * it is not, and a help tool that bluffs is worse than no help tool: somebody
 * acts on the invented answer. So it offers what it found, says plainly when
 * it found nothing, and never paraphrases beyond the text it was given.
 */

interface Turn {
  id: number;
  from: 'you' | 'assistant';
  text: string;
  /** Answers attached to an assistant turn, rendered as cards. */
  answers?: Answer[];
  /** Follow-up questions offered as chips. */
  chips?: string[];
  /** A parsed instruction, shown for confirmation before anything is written. */
  draft?: Draft;
  /** Set once the draft has been carried out, so the card stops offering to. */
  done?: boolean;
}

let nextId = 1;

export function AssistantPanel() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const can = useAuth((s) => s.can);
  const canDelete = useAuth((s) => s.canDelete);
  const orgId = useAuth((s) => s.activeOrgId);

  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  /*
   * An answer that points at a screen this person cannot open is worse than
   * no answer — it reads as the product being broken rather than as a
   * permission they were never given. Delete keys count too, so "why can I
   * not delete this" still answers for somebody who genuinely cannot.
   */
  const granted = useMemo(
    () => (feature: string) => can(feature as never) || canDelete(feature as never),
    [can, canDelete],
  );

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turns]);

  /**
   * Carrying out a confirmed draft.
   *
   * The turn is marked done rather than removed, so the panel keeps a record
   * of what it did on your behalf — an assistant that writes to your task
   * board and leaves no trace of it is the kind you stop trusting.
   */
  const carryOut = useMutation({
    mutationFn: async ({ draft: d }: { turnId: number; draft: Draft }) => {
      if (d.kind === 'task') {
        return api.post('/internal/tasks', {
          title: d.title,
          dueAt: d.dueAt ? d.dueAt.toISOString() : null,
        });
      }
      return api.post(
        '/workspace/reminders',
        { title: d.title, note: null, remindAt: d.remindAt.toISOString(), userId: null },
        orgId ? { headers: { 'X-Org-Id': orgId } } : undefined,
      );
    },
    onSuccess: (_data, { turnId, draft: d }) => {
      setTurns((t) => t.map((turn) => (turn.id === turnId ? { ...turn, done: true } : turn)));
      toast.success(d.kind === 'task' ? 'Task created' : 'Reminder set', d.title);
      void queryClient.invalidateQueries({ queryKey: ['internal-rail'] });
      void queryClient.invalidateQueries({
        queryKey: [d.kind === 'task' ? 'internal-tasks' : 'reminders'],
      });
    },
    onError: (err) =>
      toast.error(
        'Could not do that',
        err instanceof ApiRequestError ? err.message : 'Try again in a moment.',
      ),
  });

  function ask(question: string): void {
    const q = question.trim();
    if (!q) return;

    /*
     * An instruction is answered by offering to carry it out, not by finding
     * a page about it. "Remind me to call Sharma Motors tomorrow" wants the
     * reminder, not a card explaining where reminders live.
     */
    const command = parseCommand(q);
    if (command) {
      setTurns((t) => [
        ...t,
        { id: nextId++, from: 'you', text: q },
        {
          id: nextId++,
          from: 'assistant',
          text: "Here is what I read that as. Check it, then I will save it — I'd rather ask than put the wrong thing on your board.",
          draft: command,
        },
      ]);
      setDraft('');
      return;
    }

    const found = search(q, granted).map((m) => m.answer);

    const reply: Turn = found.length
      ? {
          id: nextId++,
          from: 'assistant',
          text:
            found.length === 1
              ? 'Here is what I have on that.'
              : `I found ${found.length} things that might be what you mean.`,
          answers: found,
          chips: [...new Set(found.flatMap((a) => a.related ?? []))]
            .map((id) => byId(id)?.title)
            .filter((t): t is string => Boolean(t))
            .slice(0, 3),
        }
      : {
          id: nextId++,
          from: 'assistant',
          /*
           * Said plainly. This assistant knows what was written into it and
           * nothing else, and dressing that up as "I'm not sure" invites
           * somebody to rephrase at a wall.
           */
          text: 'I do not have an answer for that one. I can find any screen in the sidebar and explain how leads, files, tasks, permissions and settings work — try naming the thing you are after. For anything beyond that, your admin is the better address.',
          chips: OPENERS.slice(0, 3),
        };

    setTurns((t) => [...t, { id: nextId++, from: 'you', text: q }, reply]);
    setDraft('');
  }

  return (
    // A flex child of the popover's column, so it takes the space the header
    // leaves rather than the panel's full height, which would push the
    // composer out of the bottom.
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {turns.length === 0 && (
          <div className="rounded-card border border-line-soft bg-surface-sunken px-3 py-3">
            <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink">
              <Sparkles size={13} className="text-vir" />
              Ask me where something is, or how it works.
            </p>
            <p className="mt-1 text-[11.5px] leading-relaxed text-ink-muted">
              I search this workspace and answer from what is built into it. I can also write a task
              or set a reminder — say &ldquo;remind me to call Sharma Motors tomorrow&rdquo; and I
              will show it to you before saving. Nothing you type here is sent anywhere to be
              understood, and I will tell you when I do not know.
            </p>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {OPENERS.map((o) => (
                <Chip key={o} label={o} onClick={() => ask(o)} />
              ))}
            </div>
          </div>
        )}

        {turns.map((turn) =>
          turn.from === 'you' ? (
            <p
              key={turn.id}
              className="ml-auto max-w-[85%] rounded-message bg-vir px-3 py-2 text-[12.5px] text-white"
            >
              {turn.text}
            </p>
          ) : (
            <div key={turn.id} className="max-w-[92%] space-y-2">
              <p className="text-[12.5px] leading-relaxed text-ink-muted">{turn.text}</p>

              {turn.answers?.map((a) => (
                <article
                  key={a.id}
                  className="rounded-card border border-line bg-white px-3 py-2.5"
                >
                  <h4 className="text-[12.5px] font-semibold text-ink">{a.title}</h4>
                  <p className="mt-1 text-[12px] leading-relaxed text-ink-muted">{a.body}</p>
                  {a.action && (
                    <button
                      type="button"
                      onClick={() => navigate(a.action!.to)}
                      className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-vir hover:underline"
                    >
                      {a.action.label}
                      <ArrowRight size={12} />
                    </button>
                  )}
                </article>
              ))}

              {turn.draft && (
                <article className="rounded-card border border-vir/40 bg-vir-light/40 px-3 py-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-vir">
                    {turn.draft.kind === 'task' ? 'New task' : 'New reminder'}
                  </p>
                  <p className="mt-1 text-[12.5px] font-semibold text-ink">{turn.draft.title}</p>
                  <p className="tabular mt-0.5 text-[11.5px] text-ink-muted">
                    {turn.draft.kind === 'task'
                      ? turn.draft.dueAt
                        ? `Due ${formatIstDateTime(turn.draft.dueAt)}`
                        : 'No due date'
                      : formatIstDateTime(turn.draft.remindAt)}
                  </p>

                  {turn.done ? (
                    <p className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-success">
                      <Check size={12} />
                      {turn.draft.kind === 'task' ? 'Created' : 'Set'}
                    </p>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        disabled={carryOut.isPending}
                        onClick={() =>
                          carryOut.mutate({ turnId: turn.id, draft: turn.draft as Draft })
                        }
                        className="rounded-control bg-vir px-2.5 py-1 text-[11.5px] font-semibold text-white transition-colors hover:bg-vir-dark disabled:opacity-60"
                      >
                        {turn.draft.kind === 'task' ? 'Create task' : 'Set reminder'}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            turn.draft?.kind === 'task' ? '/internal/tasks' : '/internal/reminders',
                          )
                        }
                        className="text-[11.5px] font-medium text-ink-soft hover:text-ink"
                      >
                        Do it myself
                      </button>
                    </div>
                  )}
                </article>
              )}

              {turn.chips && turn.chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {turn.chips.map((c) => (
                    <Chip key={c} label={c} onClick={() => ask(c)} />
                  ))}
                </div>
              )}
            </div>
          ),
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          ask(draft);
        }}
        className="flex items-center gap-2 border-t border-line px-3 py-2.5"
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Where is… / How do I…"
          aria-label="Ask the assistant"
          className="min-w-0 flex-1 rounded-control border border-line px-3 py-2 text-[12.5px] text-ink outline-none focus:border-vir"
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          aria-label="Ask"
          className={cn(
            'grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors',
            draft.trim() ? 'bg-vir text-white hover:bg-vir-dark' : 'bg-line text-ink-soft',
          )}
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}

function Chip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-pill border border-line bg-white px-2.5 py-1 text-[11px] font-medium text-ink-muted transition-colors hover:border-vir hover:text-vir"
    >
      {label}
    </button>
  );
}
